import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  TeamSession,
  TeamSessionCreate,
  TeamSessionDetail,
  TeamSessionReplaceAgents,
  TeamSessionUpdate,
} from "@/api/types";

const KEY = ["team-sessions"] as const;
const KEY_DETAIL = (id: string) => ["team-session", id] as const;

export function useTeamSessions() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.GET<TeamSession[]>("/team-sessions"),
  });
}

export function useTeamSession(id: string) {
  return useQuery({
    queryKey: KEY_DETAIL(id),
    queryFn: () => api.GET<TeamSessionDetail>(`/team-sessions/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateTeamSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TeamSessionCreate) =>
      api.POST<TeamSessionDetail>("/team-sessions", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateTeamSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: TeamSessionUpdate) =>
      api.PATCH<TeamSessionDetail>(`/team-sessions/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_DETAIL(id) });
    },
  });
}

export function useReplaceTeamSessionAgents(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TeamSessionReplaceAgents) =>
      api.PUT<TeamSessionDetail>(`/team-sessions/${id}/agents`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_DETAIL(id) });
    },
  });
}

export function useDeleteTeamSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.DELETE<void>(`/team-sessions/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
